import React, { useState, useMemo } from 'react';
import type { DocDocument, BrandConfig, AuditReport } from '../../../types/docops';
import { C } from './docopsConstants';

// Maps {{variableName}} to human-readable label and BrandConfig field
const VARIABLE_LABEL_MAP: Record<string, { field: keyof BrandConfig; label: string }> = {
  brandName:            { field: 'brandName', label: 'Brand Name' },
  brandDomain:          { field: 'brandDomain', label: 'Brand Domain' },
  brandEntity:          { field: 'brandEntity', label: 'Brand Entity (Legal Name)' },
  stateOfFormation:     { field: 'stateOfFormation', label: 'State of Formation' },
  ein:                  { field: 'ein', label: 'EIN (Tax ID)' },
  effectiveDate:        { field: 'effectiveDate', label: 'Effective Date' },
  ceoName:              { field: 'ceoName', label: 'CEO Name' },
  ceoEmail:             { field: 'ceoEmail', label: 'CEO Email' },
  medicalDirectorName:  { field: 'medicalDirectorName', label: 'Medical Director Name' },
  mdCredentials:        { field: 'mdCredentials', label: 'MD Credentials' },
  pcEntityName:         { field: 'pcEntityName', label: 'PC Entity Name' },
  pcOwnerName:          { field: 'pcOwnerName', label: 'PC Owner Name' },
  trustName:            { field: 'trustName', label: 'Trust Name' },
  trustTrusteeName:     { field: 'trustTrusteeName', label: 'Trustee Name' },
  telehealthNetwork:    { field: 'telehealthNetwork', label: 'Telehealth Network' },
  pharmacyPartner:      { field: 'pharmacyPartner', label: 'Pharmacy Partner' },
  paymentProcessor:     { field: 'paymentProcessor', label: 'Payment Processor' },
  fulfillmentPartner:   { field: 'fulfillmentPartner', label: 'Fulfillment Partner' },
  legalEmail:           { field: 'legalEmail', label: 'Legal Email' },
  privacyEmail:         { field: 'privacyEmail', label: 'Privacy Email' },
  supportEmail:         { field: 'supportEmail', label: 'Support Email' },
  complianceEmail:      { field: 'complianceEmail', label: 'Compliance Email' },
  businessAddress:      { field: 'businessAddress', label: 'Business Address' },
  arbitrationCounty:    { field: 'arbitrationCounty', label: 'Arbitration County' },
  dmcaEmail:            { field: 'dmcaEmail', label: 'DMCA Email' },
  consultationFee:      { field: 'consultationFee', label: 'Consultation Fee' },
  tier1Price:           { field: 'tier1Price', label: 'Tier 1 Price' },
};

// Bracket placeholder patterns → BrandConfig fields
const BRACKET_MAP: Record<string, { field: keyof BrandConfig; label: string }> = {
  'company name':          { field: 'brandEntity', label: 'Brand Entity (Legal Name)' },
  'business address':      { field: 'businessAddress', label: 'Business Address' },
  'medical director':      { field: 'medicalDirectorName', label: 'Medical Director Name' },
  'medical director name': { field: 'medicalDirectorName', label: 'Medical Director Name' },
  'ceo name':              { field: 'ceoName', label: 'CEO Name' },
  'effective date':        { field: 'effectiveDate', label: 'Effective Date' },
  'state':                 { field: 'stateOfFormation', label: 'State of Formation' },
  'email':                 { field: 'supportEmail', label: 'Support Email' },
  'address':               { field: 'businessAddress', label: 'Business Address' },
};

export interface DiagnosticIssue {
  field: keyof BrandConfig;
  label: string;
  severity: 'critical' | 'high' | 'medium';
  reason: string;
  currentValue: string;
}

interface RegenDiagnosticModalProps {
  doc: DocDocument;
  config: BrandConfig;
  onClose: () => void;
  onFixAndRegen: (fixes: Record<string, string>) => void;
  onRegenAnyway: () => void;
}

/** Analyze a document's content and audit report for fixable config issues */
export function diagnoseDocument(doc: DocDocument, config: BrandConfig): DiagnosticIssue[] {
  const issues: DiagnosticIssue[] = [];
  const seen = new Set<string>();

  // 1. Scan content for unreplaced {{variables}}
  if (doc.content) {
    const varMatches = doc.content.match(/\{\{([\w.]+)\}\}/g) || [];
    for (const match of varMatches) {
      const varName = match.replace(/\{\{|\}\}/g, '');
      const mapping = VARIABLE_LABEL_MAP[varName];
      if (mapping && !seen.has(mapping.field)) {
        seen.add(mapping.field);
        const current = getConfigValue(config, mapping.field);
        issues.push({
          field: mapping.field,
          label: mapping.label,
          severity: 'critical',
          reason: `{{${varName}}} appears in the document — this field is empty or missing`,
          currentValue: current,
        });
      }
    }

    // 2. Scan for [bracket placeholders]
    const bracketMatches = doc.content.match(/\[([A-Z][A-Za-z\s]+)\]/g) || [];
    for (const match of bracketMatches) {
      const inner = match.slice(1, -1).toLowerCase().trim();
      const mapping = BRACKET_MAP[inner];
      if (mapping && !seen.has(mapping.field)) {
        seen.add(mapping.field);
        const current = getConfigValue(config, mapping.field);
        if (!current) {
          issues.push({
            field: mapping.field,
            label: mapping.label,
            severity: 'high',
            reason: `[${match.slice(1, -1)}] placeholder found — needs a real value`,
            currentValue: current,
          });
        }
      }
    }
  }

  // 3. Check audit report for missing critical provisions related to entities
  if (doc.auditReport) {
    const report = doc.auditReport as AuditReport;

    // Check if brand name is missing from content
    if (report.missingCritical?.includes('References brand name') ||
        report.sections?.some(s => s.id === 'brand-name' && !s.found)) {
      if (!seen.has('brandName')) {
        seen.add('brandName');
        const current = getConfigValue(config, 'brandName');
        if (!current) {
          issues.push({
            field: 'brandName',
            label: 'Brand Name',
            severity: 'critical',
            reason: 'Document does not reference the brand name — field may be empty',
            currentValue: current,
          });
        }
      }
    }
  }

  // 4. Check key config fields that are commonly needed but empty
  const criticalFields: Array<{ field: keyof BrandConfig; label: string }> = [
    { field: 'brandName', label: 'Brand Name' },
    { field: 'brandEntity', label: 'Brand Entity (Legal Name)' },
    { field: 'medicalDirectorName', label: 'Medical Director Name' },
    { field: 'pcEntityName', label: 'PC Entity Name' },
  ];

  for (const { field, label } of criticalFields) {
    if (seen.has(field)) continue;
    const current = getConfigValue(config, field);
    if (!current && doc.content && hasEntityReference(doc.content, field)) {
      seen.add(field);
      issues.push({
        field,
        label,
        severity: 'high',
        reason: `Document references this entity but the config field is empty`,
        currentValue: '',
      });
    }
  }

  return issues;
}

function getConfigValue(config: BrandConfig, field: keyof BrandConfig): string {
  const val = config[field];
  if (val === undefined || val === null) return '';
  if (typeof val === 'string') return val;
  if (typeof val === 'number') return String(val);
  return '';
}

function hasEntityReference(content: string, field: keyof BrandConfig): boolean {
  const terms: Record<string, string[]> = {
    medicalDirectorName: ['medical director', 'supervising physician'],
    pcEntityName: ['professional corporation', 'PC', ' P.C.'],
    brandEntity: ['operating company', 'the Company'],
    trustName: ['trust', 'IP holder'],
  };
  const searchTerms = terms[field] || [];
  const lower = content.toLowerCase();
  return searchTerms.some(t => lower.includes(t.toLowerCase()));
}

export const RegenDiagnosticModal: React.FC<RegenDiagnosticModalProps> = ({
  doc, config, onClose, onFixAndRegen, onRegenAnyway,
}) => {
  const issues = useMemo(() => diagnoseDocument(doc, config), [doc, config]);
  const [values, setValues] = useState<Record<string, string>>(() => {
    const init: Record<string, string> = {};
    for (const issue of issues) {
      init[issue.field] = issue.currentValue;
    }
    return init;
  });

  const auditReport = doc.auditReport as AuditReport | undefined;
  const criticalIssues = issues.filter(i => i.severity === 'critical');
  const otherIssues = issues.filter(i => i.severity !== 'critical');

  // Audit summary info
  const missingCritical = auditReport?.missingCritical || [];
  const missingHigh = auditReport?.missingHigh || [];

  const hasFixableIssues = issues.length > 0;
  const allFilled = issues.every(i => values[i.field]?.trim());

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 1000,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'rgba(0,0,0,0.4)',
    }} onClick={onClose}>
      <div
        style={{
          background: C.bg, borderRadius: 12, padding: 24, width: 520,
          maxHeight: '80vh', overflowY: 'auto',
          boxShadow: '0 20px 60px rgba(0,0,0,0.15)', border: `1px solid ${C.border}`,
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ fontSize: 15, fontWeight: 600, color: C.text, marginBottom: 4 }}>
          Regenerate: {doc.title}
        </div>
        <div style={{ fontSize: 12, color: C.textSec, marginBottom: 12 }}>
          Current score: <strong style={{
            color: (doc.auditScore ?? 0) >= 95 ? C.success : (doc.auditScore ?? 0) >= 80 ? C.amber : C.red,
          }}>{doc.auditScore ?? '—'}%</strong>
          {auditReport && ` (attempt ${auditReport.attempt}/${auditReport.maxAttempts})`}
        </div>

        {/* Audit failures summary */}
        {(missingCritical.length > 0 || missingHigh.length > 0) && (
          <div style={{
            background: C.redBg, border: `1px solid ${C.red}`, borderRadius: 8,
            padding: 10, marginBottom: 12, fontSize: 12,
          }}>
            <div style={{ fontWeight: 600, color: C.red, marginBottom: 4 }}>
              Quality Gate Failures
            </div>
            {missingCritical.map((title, i) => (
              <div key={`c-${i}`} style={{ color: C.red }}>
                <span style={{ fontWeight: 600 }}>CRITICAL:</span> {title}
              </div>
            ))}
            {missingHigh.map((title, i) => (
              <div key={`h-${i}`} style={{ color: C.orange }}>
                <span style={{ fontWeight: 600 }}>HIGH:</span> {title}
              </div>
            ))}
          </div>
        )}

        {/* Fixable issues */}
        {hasFixableIssues ? (
          <>
            <div style={{
              fontSize: 13, fontWeight: 600, color: C.accent, marginBottom: 8,
            }}>
              {issues.length} fixable issue{issues.length > 1 ? 's' : ''} found — fill in the values below
            </div>

            {criticalIssues.length > 0 && (
              <div style={{ marginBottom: 8 }}>
                {criticalIssues.map(issue => (
                  <div key={issue.field} style={{ marginBottom: 10 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                      <span style={{
                        fontSize: 9, fontWeight: 700, padding: '1px 5px', borderRadius: 4,
                        background: C.redBg, color: C.red,
                      }}>CRITICAL</span>
                      <span style={{ fontSize: 12, fontWeight: 600, color: C.text }}>{issue.label}</span>
                    </div>
                    <div style={{ fontSize: 11, color: C.textMuted, marginBottom: 4 }}>{issue.reason}</div>
                    <input
                      type="text"
                      value={values[issue.field] || ''}
                      onChange={e => setValues(prev => ({ ...prev, [issue.field]: e.target.value }))}
                      placeholder={`Enter ${issue.label.toLowerCase()}...`}
                      style={{
                        width: '100%', padding: '6px 10px', fontSize: 13, borderRadius: 6,
                        border: `1px solid ${values[issue.field]?.trim() ? C.success : C.red}`,
                        outline: 'none', fontFamily: 'inherit',
                      }}
                    />
                  </div>
                ))}
              </div>
            )}

            {otherIssues.length > 0 && (
              <div style={{ marginBottom: 8 }}>
                {otherIssues.map(issue => (
                  <div key={issue.field} style={{ marginBottom: 10 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                      <span style={{
                        fontSize: 9, fontWeight: 700, padding: '1px 5px', borderRadius: 4,
                        background: C.amberBg, color: C.amber,
                      }}>HIGH</span>
                      <span style={{ fontSize: 12, fontWeight: 600, color: C.text }}>{issue.label}</span>
                    </div>
                    <div style={{ fontSize: 11, color: C.textMuted, marginBottom: 4 }}>{issue.reason}</div>
                    <input
                      type="text"
                      value={values[issue.field] || ''}
                      onChange={e => setValues(prev => ({ ...prev, [issue.field]: e.target.value }))}
                      placeholder={`Enter ${issue.label.toLowerCase()}...`}
                      style={{
                        width: '100%', padding: '6px 10px', fontSize: 13, borderRadius: 6,
                        border: `1px solid ${values[issue.field]?.trim() ? C.success : C.amber}`,
                        outline: 'none', fontFamily: 'inherit',
                      }}
                    />
                  </div>
                ))}
              </div>
            )}
          </>
        ) : (
          <div style={{
            background: C.successBg, border: `1px solid ${C.success}`, borderRadius: 8,
            padding: 10, marginBottom: 12, fontSize: 12, color: C.success,
          }}>
            No missing config fields detected. The low score is likely due to AI output quality — regenerating should produce a better draft.
          </div>
        )}

        {/* Actions */}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
          <button
            className="dc-btn dc-btn-secondary"
            onClick={onClose}
            style={{ fontSize: 12, padding: '6px 16px' }}
          >
            Cancel
          </button>
          {hasFixableIssues && (
            <button
              className="dc-btn dc-btn-secondary"
              onClick={onRegenAnyway}
              style={{ fontSize: 12, padding: '6px 16px', color: C.textMuted }}
            >
              Regen Without Fixing
            </button>
          )}
          <button
            className="dc-btn"
            onClick={() => {
              const fixes: Record<string, string> = {};
              for (const issue of issues) {
                const v = values[issue.field]?.trim();
                if (v && v !== issue.currentValue) {
                  fixes[issue.field] = v;
                }
              }
              onFixAndRegen(fixes);
            }}
            style={{
              fontSize: 12, padding: '6px 16px',
              background: C.btn, color: '#fff', borderRadius: 8,
              opacity: hasFixableIssues && !allFilled ? 0.7 : 1,
            }}
          >
            {hasFixableIssues ? 'Fix & Regenerate' : 'Regenerate'}
          </button>
        </div>
      </div>
    </div>
  );
};
